// B-103 — the cash differences of a day the operator creates by hand in CNN (Epago's system, which
// Mantis cannot reach): every office whose difference is beyond the client's tolerance, with its
// type and value and the cash count's difference beside it, so the operator sees whether the count
// confirms it. Each is marked "Creada en CNN" once it is, with the date (and can be undone), and the
// zone coordinator can be asked to deposit it — an email previewed before it goes, to the address
// typed (Mantis does not keep the coordinators'). "Exportar Excel" downloads the list as it is.
//
// Drawn inside "Cuadre del día", only for a client that keeps cash differences for CNN: for any
// other `listCierreCashDifferences` answers 404 and nothing is shown. Every action is the backend's
// to allow (`cierres.alerts.resolve` on the office); a refusal is said under the list.
import { useEffect, useState } from "react";
import { Button, Spinner, cn } from "@mantis-sas/ui";

import {
  listCierreCashDifferences,
  markCierreCashDifferenceCreated,
  sendCierreCashDifferenceNotice,
} from "../../../ApiCalls/mantisBusiness";
import { correctionError } from "../../../utils/cierreCorrections";
import { fechaDMY, fechaHoraBogota, montoLegible } from "../../../utils/cierrePanel";
import { CorreoAOficina } from "./CorreoAOficina";
import { descargarTablas } from "../../../utils/excelTabla";
import { guia } from "../../../utils/cierreTours";


export const TIPOS_DIFERENCIA = { SOBRANTE: "Sobrante", FALTANTE: "Faltante" };

/** The rows of the Excel, as the operator copies them into CNN. Pure. */
export function filasDeCnn(filas) {
  return [
    ["Fecha", "Código", "Oficina", "Regional", "Tipo", "Valor", "Diferencia de arqueo", "Estado", "Creada en CNN", "Registró"],
    ...filas.map((fila) => [
      fechaDMY(fila.businessDate),
      fila.officeCode,
      fila.officeName,
      fila.region || "",
      TIPOS_DIFERENCIA[fila.tipo] || fila.tipo,
      Math.abs(Number(fila.amount)),
      fila.arqueoDiferencia === null || fila.arqueoDiferencia === undefined ? "" : Number(fila.arqueoDiferencia),
      fila.estado === "CREADA_EN_CNN" ? "Creada en CNN" : "Pendiente",
      fila.cnnCreatedOn ? fechaDMY(fila.cnnCreatedOn) : "",
      fila.cnnRecordedBy || "",
    ]),
  ];
}

export function DiferenciasCnn({ token, mantisBusinessId, clientCode, fecha }) {
  const [datos, setDatos] = useState(null);
  const [intento, setIntento] = useState(0);
  const [correo, setCorreo] = useState(null); // the closing whose coordinator email is open
  const [trabajando, setTrabajando] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let activo = true;
    if (!clientCode || !fecha) return undefined;
    listCierreCashDifferences(token, mantisBusinessId, clientCode, { from: fecha, to: fecha })
      .then((res) => {
        if (activo) setDatos(res?.success ? res.data : false);
      })
      .catch(() => activo && setDatos(false));
    return () => {
      activo = false;
    };
  }, [token, mantisBusinessId, clientCode, fecha, intento]);

  if (!datos) return null;
  const filas = datos.rows || [];

  const marcar = async (fila, undo) => {
    setTrabajando(fila.closingId);
    setError(null);
    const res = await markCierreCashDifferenceCreated(token, mantisBusinessId, clientCode, { closingId: fila.closingId, undo }).catch(() => null);
    setTrabajando(null);
    if (!res?.success) {
      setError(correctionError(res) || "No se pudo guardar.");
      return;
    }
    setIntento((n) => n + 1);
  };

  const exportar = () => {
    const [encabezados, ...cuerpo] = filasDeCnn(filas);
    const formatos = ["text", "text", "text", "text", "text", "money", "money", "text", "text", "text"];
    const anchos = [12, 9, 26, 16, 11, 16, 18, 15, 14, 24];
    Promise.resolve(descargarTablas(`Diferencias CNN ${clientCode} ${fechaDMY(fecha)}.xlsx`, [{
      nombre: "CNN",
      titulo: `Diferencias de efectivo para CNN · ${fechaDMY(fecha)}`,
      subtitulo: `Sobrantes y faltantes de más de ${montoLegible(datos.tolerance)}`,
      columnas: encabezados.map((titulo, k) => ({ titulo, formato: formatos[k], ancho: anchos[k] })),
      filas: cuerpo,
    }])).catch(() => setError("No se pudo generar el Excel."));
  };

  return (
    <section {...guia("Diferencias para CNN", "Las diferencias de caja del día que pasan la tolerancia del cliente. Márcalas cuando las crees en CNN, envía el correo al coordinador o expórtalas a Excel.")} className="overflow-hidden rounded-[10px] border bg-card" aria-label="Diferencias para CNN">
      <header className="flex flex-wrap items-center justify-between gap-2 border-b bg-brinks-surface px-3 py-2">
        <div>
          <h4 className="text-[13px] font-semibold text-brinks-foreground">Diferencias para CNN</h4>
          <p className="text-[11px] text-brinks-muted-foreground">
            Sobrantes y faltantes de más de {montoLegible(datos.tolerance)}: se crean a mano en CNN y se avisa al coordinador de zona.
          </p>
        </div>
        <Button variant="outline" size="sm" className="h-7" disabled={!filas.length} onClick={exportar}>Exportar Excel</Button>
      </header>
      {!filas.length ? (
        <p className="px-3 py-2.5 text-[12px] text-brinks-muted-foreground">Ninguna oficina tiene una diferencia de efectivo para crear en CNN.</p>
      ) : (
        <ul className="divide-y divide-brinks-border" aria-label="Diferencias por oficina">
          {filas.map((fila) => {
            const creada = fila.estado === "CREADA_EN_CNN";
            return (
              <li key={fila.closingId} className="px-3 py-2.5 text-[12px]">
                <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
                  <div className="min-w-0">
                    <p className="font-semibold text-brinks-foreground">{fila.officeCode} {fila.officeName}</p>
                    {fila.region ? <p className="text-[11px] text-brinks-muted-foreground">Regional {fila.region}</p> : null}
                  </div>
                  <div className="text-right">
                    <span
                      className={cn(
                        "inline-block rounded-full px-2 py-0.5 text-[10.5px] font-semibold",
                        fila.tipo === "SOBRANTE" ? "bg-brinks-warning-soft text-brinks-warning" : "bg-brinks-danger-soft text-brinks-danger"
                      )}
                    >
                      {TIPOS_DIFERENCIA[fila.tipo] || fila.tipo}
                    </span>
                    <p className="mt-0.5 text-[14px] font-semibold tabular-nums">{montoLegible(Math.abs(Number(fila.amount)))}</p>
                  </div>
                </div>
                <dl className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-[11.5px]">
                  <div className="flex gap-1">
                    <dt className="text-brinks-muted-foreground">Diferencia de arqueo:</dt>
                    <dd className="tabular-nums">
                      {fila.arqueoDiferencia === null || fila.arqueoDiferencia === undefined ? "sin arqueo" : montoLegible(fila.arqueoDiferencia)}
                    </dd>
                  </div>
                  <div className="flex gap-1">
                    <dt className="text-brinks-muted-foreground">CNN:</dt>
                    <dd className={creada ? "text-brinks-success" : "text-brinks-warning"}>
                      {creada ? `Creada el ${fechaDMY(fila.cnnCreatedOn)}${fila.cnnRecordedBy ? ` por ${fila.cnnRecordedBy}` : ""}` : "Pendiente"}
                    </dd>
                  </div>
                  {fila.coordinatorNoticeSentAt ? (
                    <div className="flex gap-1">
                      <dt className="text-brinks-muted-foreground">Coordinador:</dt>
                      <dd>
                        avisado {fechaHoraBogota(fila.coordinatorNoticeSentAt)}
                        {fila.coordinatorNoticeSentBy ? ` por ${fila.coordinatorNoticeSentBy}` : ""}
                      </dd>
                    </div>
                  ) : null}
                </dl>
                {/* The actions in a row of their own, side by side while they fit. */}
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {trabajando === fila.closingId ? <Spinner size="sm" /> : (
                    <>
                      {creada ? (
                        <Button variant="outline" size="sm" className="h-7" onClick={() => marcar(fila, true)}>Deshacer</Button>
                      ) : (
                        <Button size="sm" className="h-7" onClick={() => marcar(fila, false)}>Marcar creada en CNN</Button>
                      )}
                      {correo !== fila.closingId ? (
                        <Button variant="outline" size="sm" className="h-7" onClick={() => setCorreo(fila.closingId)}>Correo al coordinador</Button>
                      ) : null}
                    </>
                  )}
                </div>
                {correo === fila.closingId ? (
                  <CorreoAOficina
                    titulo="Correo al coordinador de zona: consignar la diferencia"
                    textoEnviar="Enviar al coordinador"
                    ayudaPara="Para (el correo del coordinador de zona)"
                    paraObligatorio
                    enviar={(preview, { to, comment }) => sendCierreCashDifferenceNotice(token, mantisBusinessId, clientCode, {
                      closingId: fila.closingId, to, comment, preview,
                    })}
                    onCancelar={() => setCorreo(null)}
                    onEnviado={() => {
                      setCorreo(null);
                      setIntento((n) => n + 1);
                    }}
                  />
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {error ? <p role="alert" className="border-t px-3 py-2 text-[12px] text-brinks-danger">{error}</p> : null}
    </section>
  );
}

export default DiferenciasCnn;
